import {
  BuildingOffice2Icon,
  MapPinIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { Stethoscope } from 'lucide-react';
import { memo } from 'react';

import { medicalProvidersApiClient } from '@/services/api/treatmentService';
import type { MedicalProvider } from '@/services/types/treatment-types';
import {
  ContactHandleType,
  ProviderStatus,
  ProviderTreatmentStatus,
  ProviderType,
} from '@/services/types/treatment-types';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  createStatusBadge,
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

/**
 * Extra props required for MedicalProviderLink
 * The query requires both matterId and providerId
 */
export interface MedicalProviderLinkExtraProps {
  matterId: string;
}

/**
 * Get human-readable treatment status label
 */
const getTreatmentStatusLabel = (status: ProviderTreatmentStatus): string => {
  switch (status) {
    case ProviderTreatmentStatus.TREATING:
      return 'Still Treating';
    case ProviderTreatmentStatus.COMPLETED:
      return 'Completed';
    case ProviderTreatmentStatus.REFERRED_TO:
      return 'Referred To';
    case ProviderTreatmentStatus.DEVIATING:
      return 'Deviating';
    case ProviderTreatmentStatus.UNKNOWN:
    default:
      return 'Unknown';
  }
};

/**
 * Get status badge color based on treatment status
 */
const getTreatmentStatusColor = (
  status: ProviderTreatmentStatus,
): 'emerald' | 'zinc' | 'amber' | 'red' | 'blue' => {
  switch (status) {
    case ProviderTreatmentStatus.TREATING:
      return 'emerald';
    case ProviderTreatmentStatus.COMPLETED:
      return 'zinc';
    case ProviderTreatmentStatus.REFERRED_TO:
      return 'blue';
    case ProviderTreatmentStatus.DEVIATING:
      return 'amber';
    case ProviderTreatmentStatus.UNKNOWN:
    default:
      return 'zinc';
  }
};

/**
 * Get provider type label
 */
const getProviderTypeLabel = (type?: ProviderType): string | null => {
  switch (type) {
    case ProviderType.ORGANIZATION:
      return 'Organization';
    case ProviderType.PRACTITIONER:
      return 'Practitioner';
    case ProviderType.HOSPITAL:
      return 'Hospital';
    case ProviderType.EMS:
      return 'EMS';
    case ProviderType.URGENT_CARE:
      return 'Urgent Care';
    default:
      return null;
  }
};

/**
 * Get lifecycle status badge color
 */
const getStatusColor = (
  status?: ProviderStatus,
): 'emerald' | 'amber' | 'red' | 'zinc' => {
  switch (status) {
    case ProviderStatus.APPROVED:
      return 'emerald';
    case ProviderStatus.NEEDS_APPROVAL:
      return 'amber';
    case ProviderStatus.REJECTED:
      return 'red';
    default:
      return 'zinc';
  }
};

/**
 * Medical Provider tooltip content component
 * Pure presentation component - memoized for performance
 */
export const MedicalProviderTooltipContent = memo<
  EntityTooltipContentProps<MedicalProvider>
>(({ fetchState }) => {
  // Handle loading state
  if (fetchState.status === 'pending') {
    return <LoadingState rows={2} />;
  }

  // Handle error state
  if (fetchState.status === 'error') {
    return <ErrorState error={fetchState.error} entityType="provider" />;
  }

  // Handle idle or empty state - no data yet
  if (fetchState.status === 'idle') {
    return <EmptyState entityType="provider" />;
  }

  // Success state - TypeScript knows fetchState.data exists
  const provider = fetchState.data;
  const {
    name,
    address,
    treatment_status,
    provider_type,
    status,
    handles,
    treatment,
  } = provider;

  // Get phone from handles
  const phoneHandle = handles?.find(
    (h) => h.handle_type === ContactHandleType.PHONE,
  );

  const providerTypeLabel = getProviderTypeLabel(provider_type);

  return (
    <div className="divide-y divide-gray-100">
      <TooltipHeader
        icon={Stethoscope}
        title={name || 'Unknown Provider'}
        subtitle={providerTypeLabel || undefined}
      />

      <TooltipSection withBorder>
        {/* Status badges */}
        <div className="mb-2 flex flex-wrap gap-1.5">
          {treatment_status &&
            createStatusBadge({
              value: getTreatmentStatusLabel(treatment_status),
              defaultColor: getTreatmentStatusColor(treatment_status),
            })}
          {status &&
            createStatusBadge({
              value: status.replace('_', ' '),
              defaultColor: getStatusColor(status),
            })}
        </div>

        {/* Address */}
        {address && (
          <InfoRow icon={MapPinIcon} label="Address" value={address} />
        )}

        {/* Phone */}
        {phoneHandle && (
          <InfoRow icon={PhoneIcon} label="Phone" value={phoneHandle.value} />
        )}

        {/* Treatment description */}
        {treatment && (
          <InfoRow
            icon={BuildingOffice2Icon}
            label="Treatment"
            value={treatment}
          />
        )}
      </TooltipSection>
    </div>
  );
});

MedicalProviderTooltipContent.displayName = 'MedicalProviderTooltipContent';

/**
 * Hoverable medical provider link with tooltip preview
 *
 * Shows provider name, status, address, phone, and treatment info on hover.
 * Requires matterId as an extra prop since the query needs both matterId and providerId.
 *
 * @example
 * ```tsx
 * <MedicalProviderLink entityId={providerId} matterId={matterId}>
 *   {providerName}
 * </MedicalProviderLink>
 * ```
 */
export const MedicalProviderLink = createEntityLink<
  MedicalProvider,
  { matterId: string; providerId: string },
  MedicalProviderLinkExtraProps
>({
  tooltipContent: MedicalProviderTooltipContent,
  getDetailUrl: (id, { matterId }) => `/matters/${matterId}/treatment/${id}`,
  useQuery: medicalProvidersApiClient.endpoints.getMedicalProvider.useQuery,
  getQueryArg: (entityId, { matterId }) => ({
    matterId,
    providerId: entityId,
  }),
  displayName: 'MedicalProviderLink',
});
